import { Button } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { NominateApproverDialog } from '@/features/notifications/nominate-approver-dialog';
import { ACTION_LABELS, orderMessageActions } from '@/features/notifications/notifications-meta';
import { NOTIFICATION_MESSAGE_LISTS_QUERY_KEY, useReviewNotificationMessage, useSendNotificationMessage } from '@/hooks/queries/notifications/use-notification-messages';
import { getErrorMessage, HTTP_CONFLICT, HTTP_FORBIDDEN, HTTP_UNPROCESSABLE_ENTITY, isHttpStatus } from '@/lib/api/error';
import type { NotificationMessage, NotificationMessageAction } from '@/services/api/notifications';

interface MessageActionsProps {
  message: NotificationMessage;
  approvers: string[];
  onAmend: (message: NotificationMessage) => void;
}

export const MessageActions = ({ message, approvers, onAmend }: MessageActionsProps) => {
  const queryClient = useQueryClient();
  const review = useReviewNotificationMessage();
  const send = useSendNotificationMessage();
  const [isNominating, setNominating] = useState(false);

  // Server-driven: only the actions bop-api allows for this caller on this message
  const actions = orderMessageActions(message.allowedActions);
  if (actions.length === 0) return null;

  const handleWriteError = async (error: unknown, verb: string) => {
    if (isHttpStatus(error, HTTP_CONFLICT) || isHttpStatus(error, HTTP_UNPROCESSABLE_ENTITY)) {
      toast.error('The message has moved on — refreshing.');
      await queryClient.invalidateQueries({ queryKey: NOTIFICATION_MESSAGE_LISTS_QUERY_KEY });
      return;
    }
    if (isHttpStatus(error, HTTP_FORBIDDEN)) {
      toast.error(`You are not permitted to ${verb} this message.`);
      return;
    }
    toast.error(`Failed to ${verb}: ${getErrorMessage(error)}`);
  };

  const handleAction = async (action: NotificationMessageAction) => {
    if (action === 'AMEND') {
      onAmend(message);
      return;
    }
    if (action === 'SUBMIT') {
      setNominating(true);
      return;
    }

    if (action === 'LEGAL_APPROVE' || action === 'LEGAL_WITHDRAW') {
      const isApproval = action === 'LEGAL_APPROVE';
      const comment = await confirmAction({
        title: isApproval ? 'Record Legal approval?' : 'Withdraw Legal approval?',
        description: isApproval
          ? 'The message shows as approved by Legal until its content changes. Your comment is recorded in the audit trail.'
          : 'The approver will see the message as unreviewed again. Your comment is recorded in the audit trail.',
        actionLabel: ACTION_LABELS[action],
      });
      if (comment === null) return;
      try {
        await review(message.messageId, { action, comment });
        toast.success(isApproval ? 'Legal approval recorded' : 'Legal approval withdrawn');
      } catch (error) {
        await handleWriteError(error, 'record the legal review for');
      }
      return;
    }

    if (action === 'RETURN') {
      const comment = await confirmAction({
        title: 'Return to author?',
        description: 'The message goes back to draft with your comments for the author to address.',
        actionLabel: 'Return',
      });
      if (comment === null) return;
      try {
        await review(message.messageId, { action: 'RETURN', comment });
        toast.success('Returned to author');
      } catch (error) {
        await handleWriteError(error, 'return');
      }
      return;
    }

    if (action === 'CANCEL') {
      const confirmed = await confirmAction({
        title: 'Cancel this message?',
        description: 'The message is retired and can no longer be edited or sent.',
        actionLabel: 'Cancel message',
        destructive: true,
        requireJustification: false,
      });
      if (confirmed === null) return;
      try {
        await review(message.messageId, { action: 'CANCEL' });
        toast.success('Message cancelled');
      } catch (error) {
        await handleWriteError(error, 'cancel');
      }
      return;
    }

    // The acknowledgement variant is the server-driven half of the "Legal have not approved" warning
    const needsAcknowledgement = action === 'SEND_REQUIRES_ACKNOWLEDGEMENT';
    const confirmed = await confirmAction({
      title: 'Approve and send to all clients?',
      description: needsAcknowledgement
        ? 'Legal have NOT reviewed and approved this message. Sending broadcasts it to every client with notifications enabled — do you want to continue?'
        : 'Sending broadcasts this message to every client with notifications enabled.',
      actionLabel: needsAcknowledgement ? 'Send without Legal review' : 'Approve & send',
      destructive: needsAcknowledgement,
      requireJustification: false,
    });
    if (confirmed === null) return;
    try {
      await send(message.messageId, needsAcknowledgement);
      toast.success('Message sent');
    } catch (error) {
      await handleWriteError(error, 'send');
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      {actions.map((action) => (
        <Button
          key={action}
          onClick={() => handleAction(action)}
          size="sm"
          type="button"
          variant={action === 'SEND' || action === 'SEND_REQUIRES_ACKNOWLEDGEMENT' || action === 'SUBMIT' ? 'primary' : 'outline'}
        >
          {ACTION_LABELS[action]}
        </Button>
      ))}
      {isNominating ? (
        <NominateApproverDialog
          approvers={approvers}
          onClose={() => setNominating(false)}
          onNominate={async (approverEmail) => {
            try {
              await review(message.messageId, { action: 'SUBMIT', approver_email: approverEmail });
              toast.success(`Sent for approval to ${approverEmail}`);
              setNominating(false);
            } catch (error) {
              setNominating(false);
              await handleWriteError(error, 'submit');
            }
          }}
        />
      ) : null}
    </div>
  );
};
MessageActions.displayName = 'MessageActions';
